import { useAlertStore, type CrossZoneAlertEntry } from '../store/alertStore';
import { openZoneOnMap } from '../services/fleetDrillDown';
import { alertStateBadgeClass, alertTypeLabel, alertDetailText, relativeTimeText } from '../utils/alertFormat';

export function NotificationCenter() {
  const alertHistory = useAlertStore(s => s.alertHistory);
  const readKeys = useAlertStore(s => s.readKeys);
  const markRead = useAlertStore(s => s.markRead);
  const markAllRead = useAlertStore(s => s.markAllRead);
  const setNotificationCenterOpen = useAlertStore(s => s.setNotificationCenterOpen);

  const handleClickAlert = (entry: CrossZoneAlertEntry) => {
    markRead(entry.key);
    setNotificationCenterOpen(false);
    // Same rule as the Netzmonitoring page: an alert opens its zone on the live map, centred on
    // the element (same zone: only the tab changes; another zone goes through the one switch flow)
    void openZoneOnMap({ zoneId: entry.zoneId, elementId: entry.elementId });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={() => setNotificationCenterOpen(false)}
      />

      {/* Panel */}
      <div className="absolute right-0 top-full mt-1 w-96 max-h-112 bg-white dark:bg-primary-800 border border-gray-300 dark:border-primary-500 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-primary-600">
          <span className="text-sm font-semibold text-gray-900 dark:text-primary-100">
            Benachrichtigungen
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={markAllRead}
              className="text-xs text-accent-primary hover:underline"
            >
              Alle als gelesen
            </button>
            <button
              onClick={() => setNotificationCenterOpen(false)}
              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-primary-600 text-gray-400 dark:text-primary-400"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Alert list */}
        <div className="overflow-y-auto flex-1">
          {alertHistory.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-primary-400">
              Keine Alarme in den letzten 24 Stunden
            </div>
          ) : (
            alertHistory.map((entry) => {
              const isRead = readKeys.has(entry.key);
              const isResolved = !!entry.resolvedAt;
              return (
              <div
                key={entry.key}
                onClick={() => handleClickAlert(entry)}
                className={`px-4 py-2.5 border-b border-gray-100 dark:border-primary-700 hover:bg-gray-50 dark:hover:bg-primary-700 cursor-pointer transition-colors ${isRead || isResolved ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {isResolved ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-600 text-white">
                      Behoben
                    </span>
                  ) : (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${alertStateBadgeClass(entry.newState)}`}>
                      {entry.newState}
                    </span>
                  )}
                  <span className={`text-xs font-medium text-gray-900 dark:text-primary-100 ${isResolved ? 'line-through' : ''}`}>
                    {entry.zoneName}
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-primary-500 ml-auto">
                    {alertTypeLabel(entry)}
                  </span>
                </div>
                <div className={`text-xs text-gray-700 dark:text-primary-300 ${isResolved ? 'line-through' : ''}`}>
                  {alertDetailText(entry)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-primary-500 mt-0.5">
                  {relativeTimeText(entry.timestamp)}
                  {isResolved && ` — behoben ${relativeTimeText(entry.resolvedAt!)}`}
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
